import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Package, Check, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SubscriptionPage({ user, billing, onLogout }) {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const pct = billing?.discount_percent || 0;
  const price = (base) => Math.round(base * (100 - pct)) / 100;

  const subscribe = async (plan) => {
    setLoadingPlan(plan);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Check your connection and try again.");
      setLoadingPlan(null);
    }
  };

  const plans = [
    { key: "monthly", label: "Monthly", base: 20, per: "/month", note: "Billed every month" },
    { key: "yearly", label: "Yearly", base: 180, per: "/year", note: "Save $60 vs monthly", best: true },
  ];

  return (
    <div className="min-h-screen bg-[#18181b] flex items-center justify-center p-4" data-testid="subscription-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <Package className="w-7 h-7 text-orange-500" />
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Pallet Cut Calculator</h1>
        </div>
        <div className="bg-white border border-zinc-200 p-6">
          <h2 className="text-lg font-bold text-zinc-900 mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>
            {billing?.trial_days_left === 0 || !billing?.trial_ends_at ? "Your free trial has ended" : "Subscribe to keep using the app"}
          </h2>
          <p className="text-sm text-zinc-500 mb-1">Signed in as <span className="font-semibold break-all">{user?.email}</span></p>
          <p className="text-sm text-zinc-500 mb-4">Pick a plan to unlock the full app on all your devices.</p>
          {pct > 0 && (
            <p className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-1.5 mb-4" data-testid="discount-banner">
              {pct}% discount applied to your account
            </p>
          )}
          <div className="space-y-3">
            {plans.map((p) => (
              <button key={p.key} onClick={() => subscribe(p.key)} disabled={loadingPlan !== null}
                className={`w-full text-left p-4 border-2 transition-colors ${p.best ? "border-orange-500 bg-orange-50/50 hover:bg-orange-50" : "border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50"} disabled:opacity-60`}
                data-testid={`subscribe-${p.key}-btn`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-zinc-900 uppercase tracking-wider">{p.label}{p.best && <span className="ml-2 text-[10px] bg-orange-500 text-white px-1.5 py-0.5">BEST VALUE</span>}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{p.note}</p>
                  </div>
                  <div className="text-right">
                    {pct > 0 && <p className="text-xs text-zinc-400 line-through">${p.base}</p>}
                    <p className="text-2xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
                      ${price(p.base)}<span className="text-xs font-normal text-zinc-400">{p.per}</span>
                    </p>
                  </div>
                </div>
                {loadingPlan === p.key && <p className="text-xs text-orange-600 mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Opening secure checkout…</p>}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-1">
            {["All 4 pages: Calculator, Cut List, Lumber, Orders", "Real-time sync across all your devices"].map((f) => (
              <p key={f} className="text-xs text-zinc-500 flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />{f}</p>
            ))}
          </div>
        </div>
        <button onClick={onLogout} className="mt-4 mx-auto flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="paywall-signout-btn">
          <LogOut className="w-4 h-4" />Sign out
        </button>
      </div>
    </div>
  );
}
